export function isArenaDebugMode() {
  try {
    return !!import.meta.env?.DEV;
  } catch {
    return false;
  }
}

export const CURRENCY = {
  SELL_ORDINARY: 2,
  SELL_EXCEPTIONAL: 5,
  SELL_ELITE: 10,
  SELL_UNIQUE: 25,
  PACK_PRICE: 50,
};

// Arcana Shards: secondary currency for targeted, single-card purchases.
// Mirrors the server's authoritative price table in
// valkenhall-server/src/utils/shardPrices.js. The server always validates
// the price on a purchase request, this is display-only.
export const SHARD_PRICES = {
  Ordinary: 50,
  Exceptional: 150,
  Elite: 220,
  Unique: 720,
};

export function shardPriceForRarity(rarity) {
  return SHARD_PRICES[rarity] ?? null;
}

// Disenchant refund. Must match valkenhall-server/src/utils/shardPrices.js.
// Fixed per-rarity payout so craft-cost tuning doesn't drag refunds along.
export const SHARD_REFUNDS = {
  Ordinary: 3,
  Exceptional: 9,
  Elite: 16,
  Unique: 49,
};

export function disenchantRefundForRarity(rarity) {
  return SHARD_REFUNDS[rarity] ?? null;
}

export const SET_UNLOCK_LEVELS = {
  beta: 1,
  arthurian: 1,
  gothic: 1,
};

// Player level curve. Designed against the per-match XP rates the server
// hands out (duration-scaled, ~3.4 XP/min capped at 60 min. See
// valkenhall-server/src/utils/rewards.js). L30 is the current cap; the
// final deck-progression milestone is L25 (8 decks total: L1 starter
// plus picks at L2/L3/L5/L10/L15/L20/L25). The curve is linear from
// L1 → L30 and was scaled by 1.6× when XP moved off the flat 100/50
// formula; existing balances were rescaled by the same factor on
// deploy so the matches-per-level cadence is preserved:
//
//   - L2  ≈ 1 match    (~160 XP)            → first pack + deck #2
//   - L3  ≈ 2 matches  (~320 XP cumulative) → deck #3
//   - L5  ≈ 6 matches  (~880 XP cumulative) → deck #4
//   - L10 ≈ 18 matches (~2880 XP)           → deck #5
//   - L15 ≈ 35 matches (~5600 XP)           → deck #6
//   - L20 ≈ 60 matches (~9600 XP)           → deck #7
//   - L25 ≈ 95 matches (~15200 XP)          → deck #8 (full set)
//
// When tuning the per-match XP, keep these matches-per-level numbers in
// mind so the level-up cadence stays the goal of the curve.
export const MAX_LEVEL = 30;
const XP_CAP_LEVEL = MAX_LEVEL;

const XP_BASE = 160;
const XP_CAP = 1280;
const XP_STEP = (XP_CAP - XP_BASE) / (XP_CAP_LEVEL - 1);

function xpToNextLevel(level) {
  if (level < 1) return XP_BASE;
  if (level >= XP_CAP_LEVEL) return XP_CAP;
  return Math.floor(XP_BASE + XP_STEP * (level - 1));
}

const XP_TABLE = [];
(function buildXpTable() {
  let cumulative = 0;
  XP_TABLE[0] = 0;
  XP_TABLE[1] = 0;
  for (let lvl = 2; lvl <= MAX_LEVEL + 1; lvl++) {
    cumulative += xpToNextLevel(lvl - 1);
    XP_TABLE[lvl] = cumulative;
  }
})();

export function xpForLevel(level) {
  if (level <= 1) return 0;
  if (level > MAX_LEVEL + 1) return XP_TABLE[MAX_LEVEL + 1];
  return XP_TABLE[level];
}

export function levelFromXp(xp) {
  const safeXp = Math.max(0, xp || 0);
  for (let lvl = MAX_LEVEL; lvl >= 1; lvl--) {
    if (safeXp >= XP_TABLE[lvl]) return Math.min(lvl, MAX_LEVEL);
  }
  return 1;
}

// ── Progression-screen mirror helpers ──────────────────────────────
// Mirror of server constants from valkenhall-server/src/utils/progression.js.
// The screen receives the authoritative snapshot from the server on
// open + after every claim; these helpers are for synchronous reads
// during render (e.g. the hub-pill gold-dot indicator that needs to
// fire without an API round-trip).

export const GOLD_PER_LEVEL = 50;
export const SKILL_POINT_PER_GRANT = 1;
export const TOTAL_SKILL_POINTS_CAP = 15;

export const GOLD_GRANT_LEVELS = (() => {
  const arr = [];
  for (let lvl = 2; lvl <= MAX_LEVEL; lvl++) arr.push(lvl);
  return arr;
})();

export const SKILL_POINT_GRANT_LEVELS = [
  2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30,
];

export const DECK_GRANT_LEVELS = [1, 2, 3, 5, 10, 15, 20, 25];

export function nextUnclaimedGoldLevelClient(lastClaimedGoldLevel) {
  const claimed = Math.max(0, lastClaimedGoldLevel || 0);
  for (const lvl of GOLD_GRANT_LEVELS) {
    if (lvl > claimed) return lvl;
  }
  return null;
}

export function nextUnclaimedSkillPointLevelClient(grantedSkillPoints) {
  const granted = Math.max(0, grantedSkillPoints || 0);
  return SKILL_POINT_GRANT_LEVELS[granted] ?? null;
}

export function nextUnclaimedDeckLevelClient(claimedDeckCount) {
  const claimed = Math.max(0, claimedDeckCount || 0);
  return DECK_GRANT_LEVELS[claimed] ?? null;
}

// Quick yes/no check against an arenaProfile snapshot. Does the
// player have any reward currently waiting to be claimed? Drives the
// gold-dot indicator on the hub's level pill.
export function hasAnyPendingClaim(arenaProfile) {
  if (!arenaProfile) return false;
  const xp = arenaProfile.xp || 0;
  const level = levelFromXp(xp);
  const lastGold = arenaProfile.progression?.lastClaimedGoldLevel
    ?? arenaProfile.lastClaimedGoldLevel
    ?? 1;
  const grantedSp = arenaProfile.progression?.grantedSkillPoints
    ?? arenaProfile.grantedSkillPoints
    ?? 0;
  const owned = arenaProfile.starterDecksOwned?.length ?? 0;
  const nextGold = nextUnclaimedGoldLevelClient(lastGold);
  if (nextGold !== null && nextGold <= level) return true;
  const nextSp = nextUnclaimedSkillPointLevelClient(grantedSp);
  if (nextSp !== null && nextSp <= level) return true;
  const nextDeck = nextUnclaimedDeckLevelClient(owned);
  if (nextDeck !== null && nextDeck <= level) return true;
  return false;
}

export function xpProgressInLevel(xp) {
  const safeXp = Math.max(0, xp || 0);
  const level = levelFromXp(safeXp);
  // At MAX_LEVEL there's no "next level" to advance toward; the server
  // also stops accumulating xp past this point. Report the bar as full
  // and flag isMax so callers can render "MAX" instead of a percentage.
  if (level >= MAX_LEVEL) {
    return { level, current: 0, needed: 0, fraction: 1, isMax: true };
  }
  const currentLevelXp = xpForLevel(level);
  const nextLevelXp = xpForLevel(level + 1);
  const range = nextLevelXp - currentLevelXp;
  const current = Math.max(0, safeXp - currentLevelXp);
  return {
    level,
    current,
    needed: range,
    fraction: range > 0 ? current / range : 0,
    isMax: false,
  };
}

export function createDefaultProfile() {
  return {
    id: `arena-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: null,
    coins: 0,
    xp: 0,
    arcanaShards: 0,
    // Keystones: third currency, granted 1-per-achievement, spendable
    // as a free draft entry in lieu of the gold cost.
    keystones: 0,
    starterDeck: null,
    // Every precon deck the player has earned via the progression
    // flow, ordered from first pick (L1 starter) to latest milestone.
    // The server is authoritative. These fields are reset from
    // /profile/me on every load. But seeding them here gives the
    // rest of the UI a stable shape to read from before that first
    // load completes, preventing "pendingDeckGrants is undefined"
    // flashes in the picker gate.
    starterDecksOwned: [],
    pendingDeckGrants: 0,
    profileAvatar: null,
    serverToken: null,
    serverRegistered: false,
    rank: { tier: 'apprentice', division: 4, lp: 0 },
    collection: [],
    matchHistory: [],
    achievements: [],
    // Subset of achievements the player has earned but not yet
    // clicked-to-claim. Drives the pulsing affordance + celebration
    // overlay in the achievements panel.
    unclaimedAchievements: [],
    seasonProgress: null,
  };
}
